/*
 * Copyright (C) 2021 Quartet Health, Inc - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 */

/**
 * For a comprehensive review on how SVG Paths are generated, please review:
 *
 * https://developer.mozilla.org/en-US/docs/Web/SVG/Tutorial/Paths
 */

import {
  ViewBoxWidth,
  ViewBoxHeight
} from 'src/components/LandingPage/medicaid/WaveForms/constants';

/**
 * "V" draws a Vertical line based on the yAxisReturn value. A positive y-axis value to continue further "down"
 * "H" draws a Hozicontal line based on the xAxisReturn value. A negative x-axis value heads towards the "left".
 * "Z" completes the SVG path by linking the last point drawn back to the first point drawn.
 */
const fillBottomWave = ({
  yAxisReturn,
  xAxisReturn
}: {
  yAxisReturn: number;
  xAxisReturn: number;
}) => {
  return `V ${yAxisReturn} H -${xAxisReturn}Z`;
};

/**
 *  Draws a calming wave
 *    _
 *  /   \        _
 * /     \     /   \
 *        \   /
 *          -
 *
 * Creates an initial wave (up and down Bezier curves) and creates an additional
 * Bezier curve whose ending point is outside the viewport. The peak of the
 * ending Bezier curve is lower than the first up wave.
 */
export const createCalmingWavePath = ({
  shouldInvertWave
}: {
  shouldInvertWave?: boolean;
}) => {
  // The first wave ends 3/4ths of the way into the screen
  const firstWaveEndPoint = ViewBoxWidth * 0.75;

  // The second wave starts where the first wave ends.
  // Second wave ends past the viewbox by 1/4 of the width.
  const secondWaveEndPoint = ViewBoxWidth * 1.085;

  const FirstWaveStart = {
    x: 0,
    y: ViewBoxHeight / 2
  };

  const FirstWaveEnd = {
    x: firstWaveEndPoint,
    y: ViewBoxHeight / 2
  };

  const SecondWaveEnd = {
    x: secondWaveEndPoint,
    // We want the wave to end closer to the bottom of the viewbox
    // to make the top of the wave also lower
    y: ViewBoxHeight * 0.9
  };

  /**
   * First Wave
   */
  const startFirstWave = `M${FirstWaveStart.x} ${FirstWaveStart.y}`;

  const firstControlPoint = {
    x: FirstWaveEnd.x / 4,
    y: (ViewBoxHeight / 2) * -1
  };

  firstControlPoint.y = shouldInvertWave
    ? ViewBoxHeight + ViewBoxHeight / 2
    : firstControlPoint.y;

  const controlPoint1 = `Q${firstControlPoint.x} ${firstControlPoint.y} ${
    FirstWaveEnd.x / 2
  } ${FirstWaveEnd.y}`;

  const endOfFirstWave = `T${FirstWaveEnd.x} ${FirstWaveEnd.y}`;

  /**
   * Second Wave
   * Starting position is the end of the First Wave
   **/
  const secondControlPoint = {
    x: FirstWaveEnd.x + (SecondWaveEnd.x - FirstWaveEnd.x) / 2,
    y: (ViewBoxHeight / 4) * -1
  };

  secondControlPoint.y = shouldInvertWave
    ? ViewBoxHeight + ViewBoxHeight / 4
    : secondControlPoint.y;

  const controlPoint2 = `Q${secondControlPoint.x} ${secondControlPoint.y} ${SecondWaveEnd.x} ${SecondWaveEnd.y}`;

  /**
   * Carriage Return
   */
  const bottom = fillBottomWave({
    yAxisReturn: ViewBoxHeight,
    xAxisReturn: secondWaveEndPoint
  });

  return `${startFirstWave} ${controlPoint1} ${endOfFirstWave} ${controlPoint2} ${bottom}`;
};

/**
 *  Draws a simple wave
 *    _
 *  /   \
 * /     \
 *        \   /
 *          -
 *
 * Creates a wave (up and down Bezier curves) that begin and end on the same
 * y-axis point.
 */
export const createSimpleWavePath = ({
  shouldInvertWave
}: {
  shouldInvertWave?: boolean;
}) => {
  const WaveStart = {
    x: 0,
    y: ViewBoxHeight / 2
  };

  const WaveEnd = {
    x: ViewBoxWidth,
    y: ViewBoxHeight / 2
  };

  const startWave = `M${WaveStart.x} ${WaveStart.y}`;
  const endOfReflectedWave = `T${WaveEnd.x} ${WaveEnd.y}`;

  const controlPoint = {
    x: ViewBoxWidth / 4,
    y: (ViewBoxHeight / 2.5) * -1
  };

  controlPoint.y = shouldInvertWave
    ? ViewBoxHeight + ViewBoxHeight / 2
    : controlPoint.y;

  const controlPoint1 = `Q${controlPoint.x} ${controlPoint.y} ${
    ViewBoxWidth / 2
  } ${WaveEnd.y}`;
  const bottom = fillBottomWave({
    xAxisReturn: ViewBoxWidth,
    yAxisReturn: ViewBoxHeight
  });

  return `${startWave} ${controlPoint1} ${endOfReflectedWave} ${bottom}`;
};
